import Pusher from "pusher-js"

class PusherManager {
  constructor() {
    this.pusher = null
    this.channels = new Map()
    this.callbackRegistry = new Map()
    this.channelPrefix = null
  }

  initialize() {
    const appKey = document.querySelector('meta[name="pusher-key"]')?.content
    const cluster = document.querySelector('meta[name="pusher-cluster"]')?.content
    this.channelPrefix = document.querySelector('meta[name="pusher-channel-prefix"]')?.content

    if (!appKey || !cluster) {
      console.warn("PusherManager: Missing Pusher credentials in meta tags")
      return false
    }

    this.pusher = new Pusher(appKey, { cluster })

    return true
  }

  isInitialized() {
    return this.pusher !== null
  }

  subscribeOnNewDialogMessage(universityId, callback) {
    const channel = this.getOrCreateChannel("dashboard")
    const eventName = `messages:new:${universityId}`

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeOnDeletedDialogMessage(universityId, callback) {
    const channel = this.getOrCreateChannel("dashboard")
    const eventName = `messages:deleted:${universityId}`

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeOnReportedDialogMessage(universityId, callback) {
    const channel = this.getOrCreateChannel("dashboard")
    const eventName = `messages:reported:${universityId}`

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatReactions(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-reaction-toggle"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatPinMessage(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-pin-message"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatUnpinMessage(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-unpin-message"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatDeleteMessage(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-delete-message"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatUserBlocked(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-blocked"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatUserUnblocked(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-unblocked"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatCloseToggle(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-close-toggle"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatActiveToggle(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-active-toggle"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToChatArchiveToggle(chatId, callback) {
    const channel = this.getOrCreateChannel(`chat-${chatId}`)
    const eventName = "chat-archive-toggle"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToLiveEventStatusChanged(liveEventId, callback) {
    const channel = this.getOrCreateChannel(`live-stream-${liveEventId}`)
    const eventName = "status-changed"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToLiveEventSpeakerRemoved(liveEventId, callback) {
    const channel = this.getOrCreateChannel(`live-stream-${liveEventId}`)
    const eventName = "speaker-removed"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToLiveEventVideoReady(liveEventId, callback) {
    const channel = this.getOrCreateChannel(`live-stream-${liveEventId}`)
    const eventName = "video-ready"

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToKeywordDetected(universityId, callback) {
    const channel = this.getOrCreateChannel("dashboard")
    const eventName = `keyword:new:${universityId}`

    channel.bind(eventName, callback)
    this.callbackRegistry.set(callback, { channel, eventName })
  }

  subscribeToBlockedProspect(callback) {
    const channel = this.getOrCreateChannel("tap-page")

    const wrappedCallback = (eventName, data) => {
      if (eventName.includes(":messages:new") && data?.blocked_prospect_id) {
        callback(data)
      }
    }

    channel.bind_global(wrappedCallback)
    this.callbackRegistry.set(callback, { channel, eventName: "_global_", wrappedCallback })
  }

  unsubscribe(callback) {
    const registration = this.callbackRegistry.get(callback)
    if (registration) {
      if (registration.eventName === "_global_") {
        registration.channel.unbind_global(registration.wrappedCallback)
      } else {
        registration.channel.unbind(registration.eventName, callback)
      }
      this.callbackRegistry.delete(callback)
    }
  }

  getOrCreateChannel(channelName) {
    const nameWithPrefix = `${this.channelPrefix}${channelName}`

    console.log(`PusherManager: Subscribing to channel ${nameWithPrefix}`)

    if (!this.channels.has(nameWithPrefix)) {
      const channel = this.pusher.subscribe(nameWithPrefix)
      this.channels.set(nameWithPrefix, channel)
    }

    return this.channels.get(nameWithPrefix)
  }
}

export default new PusherManager()
